<script setup lang="ts">
    import { getEventColor } from "@/tabs/Operation/colors-utils";
    // Ignores behövs, pga kaosiga versioner av TS, Vite, Vue osv...
    // @ts-ignore
    import { EventImpl } from "@fullcalendar/core/internal";
    import { computed, ref, toRefs, defineEmits } from "vue";
    import moment from "moment";

    /**
     * "Pop-up" som visas vid klick på event
     */

    const props = withDefaults(
        defineProps<{
            // Ska stäng-knappen visas?
            showCloseIcon?: boolean;
            // Ska det gå att stänga panel?
            dismissable?: boolean;
            // Selector för element som panel ska attachas till
            appendTo?: string;
            // Namn på variabel som används för färgsättning
            colorKey?: string;
            // Eventet från Fullcalendar
            event: EventImpl;
            // Data som motsvarar eventet
            data: unknown;
            // Ska färgklicken högst upp renderas?
            showColorMark?: boolean;
            // Ska datum renderas?
            showDate?: boolean;
            // Storlek på font. Default 0.9rem
            fontSize?: string;
        }>(),
        {
            showCloseIcon: false,
            dismissable: true,
            appendTo: "body",
            colorKey: "",
            showColorMark: true,
            showDate: true,
            fontSize: "0.9rem",
        }
    );

    const emit = defineEmits(["show", "hide"]);

    const { colorKey, event, data } = toRefs(props);

    const panelRef = ref();

    const color = computed(() => {
        // Om colorIndex är satt ska den overridea ev färg satt av aktuell colorKey
        if (typeof event?.value?.extendedProps?.colorIndex === "number") {
            return getEventColor(Number(event.value.extendedProps.colorIndex));
        }

        return getEventColor(Number(event?.value?.extendedProps?.[colorKey.value] ?? 0));
    });
    const colorMark = computed(() => color?.value?.backgroundColor);

    const toggle = (event: MouseEvent) => {
        panelRef?.value?.toggle?.(event);
    };

    /**
     * Hämtar bara starttid eller start och slut, om sluttiden är samma dag
     * @param event
     */
    const getStartEndTime = ({ event }: { event: EventImpl }) => {
        return `${moment(event.start).format("HH:mm").toString()}${
            moment(event.start).isSame(moment(event.end), "day")
                ? ` - ${moment(event.end).format("HH:mm").toString()}`
                : ""
        }`;
    };

    const getEndTime = ({ event }: { event: EventImpl }) => {
        return `${moment(event.end).format("HH:mm").toString()}`;
    };

    const getDate = ({ date }: { date: Date | null }) => {
        if (!date) {
            return "";
        }

        return `${new Date(date).toLocaleDateString("sv", {
            weekday: "long",
            month: "long",
            year: "numeric",
            day: "numeric",
        })}`;
    };

    const onShow = () => {
        emit("show");
    };

    const onHide = () => {
        emit("hide");
    };

    defineExpose({
        toggle,
    });
</script>

<template>
    <PrimeOverlayPanel
        ref="panelRef"
        class="overlay-panel"
        :dismissable="dismissable"
        :showCloseIcon="showCloseIcon"
        :appendTo="appendTo"
        :style="{ fontSize }"
        @show="onShow"
        @hide="onHide"
    >
        <div class="header">
            <!-- Behöver sätta colorMark via style, pga att v-bind i css inte fungerar i v-deep -->
            <div v-if="showColorMark" class="color-mark" :style="{ backgroundColor: colorMark }" />
            <div v-if="showDate" class="dates">
                <div class="first">
                    <span class="date">{{ getDate({ date: event.start }) }}</span>
                    <span>{{ getStartEndTime({ event }) }}</span>
                </div>
                <div v-if="!moment(event.start).isSame(moment(event.end), 'day')" class="second">
                    <span class="date">{{ getDate({ date: event.end }) }}</span>
                    <span>{{ getEndTime({ event }) }}</span>
                </div>
            </div>
            <slot name="header" />
        </div>
        <div class="content">
            <slot name="content" />
        </div>
        <div v-if="$slots.actions" class="actions">
            <slot name="actions" />
        </div>
    </PrimeOverlayPanel>
</template>

<style scoped lang="scss">
    @import "@/style/variables";

    .overlay-panel {
        font-family: Roboto, sans-serif;
        border-radius: $border-radius-pop-up;
        box-shadow: $box-shadow-pop-up;
        border: 1px solid $panel-border-color;
        width: 25rem;
        max-width: 100%;

        &::before,
        &::after {
            content: "";
            display: none;
        }

        ::v-deep .p-overlaypanel-content {
            $gap: 0.7rem;

            padding: 1.5rem;
            display: flex;
            gap: $gap;
            flex-direction: column;

            .header {
                .color-mark {
                    height: 0.375rem;
                    width: 3.75rem;
                    border-radius: $border-radius;
                    margin-bottom: 0.375rem;
                }

                .dates {
                    display: grid;
                    font-size: 1rem;

                    .first,
                    .second {
                        display: flex;
                        justify-content: space-between;

                        .date:first-letter {
                            text-transform: uppercase;
                        }
                    }
                }
            }

            .actions {
                border-top: 1px solid $divider-color;
                padding-top: $gap;
            }
        }
    }
</style>
